import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { getToken } from "@/lib/auth";

// ── Helpers ───────────────────────────────────────────────────────────────────

function trendArrow(change) {
  if (change === null || change === undefined) return null;
  const up = change >= 0;
  return (
    <span className={`text-xs font-medium ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? "▲" : "▼"} {Math.abs(change)}%
    </span>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KPICard({ label, value, sub, accent, change }) {
  return (
    <div className="glass rounded-2xl p-5 flex flex-col gap-1">
      <div className="text-[11px] tracking-[0.22em] uppercase text-white/40">{label}</div>
      <div className={`font-display text-4xl ${accent ? "text-emerald-400" : "text-white"}`}>
        {value ?? "—"}
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        {sub  && <span className="text-sm text-white/40">{sub}</span>}
        {change !== undefined && trendArrow(change)}
        {change === undefined && <span className="text-xs text-white/20">vs yesterday</span>}
      </div>
    </div>
  );
}

function ConversionCard({ from, to, rate }) {
  const color = rate >= 50 ? "text-emerald-400" : rate >= 20 ? "text-[#D4AF37]" : "text-red-400";
  return (
    <div className="glass rounded-2xl p-5">
      <div className="text-[11px] tracking-[0.2em] uppercase text-white/35 mb-1">
        {from} → {to}
      </div>
      <div className={`font-display text-4xl ${color}`}>{rate ?? "—"}%</div>
    </div>
  );
}

function FunnelBar({ label, count, pct, stepRate, isLast }) {
  const barColor = isLast ? "bg-emerald-400" : "bg-[#D4AF37]";
  return (
    <div>
      <div className="flex justify-between items-end mb-1.5">
        <span className="text-sm text-white/70">{label}</span>
        <div className="flex items-center gap-3 text-right">
          {stepRate !== null && stepRate !== undefined && (
            <span className="text-xs text-white/35">
              {stepRate}% from prev
            </span>
          )}
          <span className="text-sm font-medium text-white">{count}</span>
          <span className="text-xs text-white/40 w-10 text-right">{pct}%</span>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-white/8">
        <div
          className={`h-2.5 rounded-full ${barColor} transition-all`}
          style={{ width: `${Math.max(pct, 0.5)}%` }}
        />
      </div>
    </div>
  );
}

function TrafficBadge({ type, count }) {
  const styles = {
    auth: "bg-[rgba(52,211,153,0.1)] text-emerald-400",
    anon: "bg-white/5 text-white/50",
    bot:  "bg-[rgba(239,68,68,0.1)] text-red-400",
  };
  return (
    <div className={`rounded-xl px-4 py-3 ${styles[type] || "bg-white/5 text-white/50"}`}>
      <div className="text-[10px] uppercase tracking-wider mb-0.5 opacity-70">{type}</div>
      <div className="font-display text-2xl">{count ?? 0}</div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminIntelligence() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    const token = getToken();
    fetch("/api/admin/intelligence", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError("Failed to load: " + e); setLoading(false); });
  }, []);

  if (loading) return <AdminShell><div className="p-10 text-white/40">Loading…</div></AdminShell>;
  if (error)   return <AdminShell><div className="p-10 text-red-400">{error}</div></AdminShell>;
  if (!data)   return null;

  const f  = data.funnel         || {};
  const t  = data.traffic        || {};
  const tr = data.trends         || {};
  const an = data.anomaly        || {};
  const at = data.attempts       || {};
  const r  = f.rates             || {};
  const now24  = tr.last_24h     || {};
  const chg    = tr.change       || {};
  const splits = data.traffic_splits || {};
  const startSplit  = splits['onboarding_start']   || {};
  const blockSplit  = splits['onboarding_blocked']  || {};

  const totalTraffic = (t.auth || 0) + (t.anon || 0) + (t.bot || 0);
  const botPct = totalTraffic > 0 ? Math.round((t.bot / totalTraffic) * 100) : 0;

  return (
    <AdminShell>
      <div className="p-6 md:p-10 text-white space-y-10">

        {/* Header */}
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Admin</div>
          <h1 className="font-display text-3xl">Growth Intelligence</h1>
          <p className="text-white/35 text-sm mt-1">Conversion · Funnel · Traffic Quality · Recovery</p>
        </div>

        {/* ── Section 1: 24h Activity + Trends ─────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-4">Activity — Last 24h</h2>
          <div className="grid grid-cols-3 gap-4">
            <KPICard
              label="Started Onboarding"
              value={now24['onboarding_start'] ?? 0}
              sub="today"
              change={chg['onboarding_start']}
            />
            <KPICard
              label="Blocked"
              value={now24['onboarding_blocked'] ?? 0}
              sub="today"
              change={chg['onboarding_blocked']}
            />
            <KPICard
              label="Completed"
              value={now24['onboarding_complete'] ?? 0}
              sub="today"
              accent
              change={chg['onboarding_complete']}
            />
          </div>
        </section>

        {/* ── Section 2: Conversion Rates ───────────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-4">Conversion Rates (lifetime)</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <ConversionCard from="Start"   to="Basics"   rate={r.start_to_s0} />
            <ConversionCard from="Basics"  to="Intent"   rate={r.s0_to_s1} />
            <ConversionCard from="Intent"  to="Values"   rate={r.s1_to_s2} />
            <ConversionCard from="Values"  to="Video"    rate={r.s2_to_s3} />
            <ConversionCard from="Video"   to="Complete" rate={r.s3_to_complete} />
            <div className="glass rounded-2xl p-5 border border-[rgba(212,175,55,0.2)]">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-1">Overall</div>
              <div className="font-display text-4xl text-[#D4AF37]">{r.overall ?? "—"}%</div>
              <div className="text-xs text-white/35 mt-1">start → complete</div>
            </div>
          </div>
        </section>

        {/* ── Section 3: Traffic Sources ───────────────────────────────────── */}
        {data.sources?.length > 0 && (
          <section>
            <h2 className="font-display text-xl mb-4">Traffic Sources</h2>
            <div className="glass rounded-2xl p-5 space-y-3">
              {data.sources.map((s) => {
                const max = data.sources[0]?.count || 1;
                const pct = Math.round(((s.count || 0) / max) * 100);
                return (
                  <div key={s._id ?? "null"} className="flex items-center gap-3">
                    <span className="text-sm text-white/60 w-32 shrink-0 truncate">{s._id || "direct"}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-white/8 overflow-hidden">
                      <div className="h-2.5 rounded-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-semibold w-10 text-right">{s.count}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Section 3b: Blocked Users by Source (Today) ──────────────────── */}
        {data.blocked_by_source?.length > 0 && (
          <section>
            <h2 className="font-display text-xl mb-4">Blocked Users by Source <span className="text-sm font-sans text-white/35 ml-2">Today</span></h2>
            <div className="glass rounded-2xl p-5 space-y-3">
              {data.blocked_by_source.map((row) => (
                <div key={row.source ?? "null"} className="flex items-center gap-3">
                  <span className="text-sm text-white/60 w-32 shrink-0 truncate">{row.source || "direct"}</span>
                  <div className="flex-1 flex items-center gap-2">
                    <span className="text-sm font-semibold text-orange-400">
                      {row.unique_users} {row.unique_users === 1 ? "user" : "users"}
                    </span>
                    <span className="text-xs text-white/30">({row.events} events)</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Section 4: Funnel Visualization ──────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-1">Conversion Funnel</h2>
          <p className="text-white/35 text-sm mb-4">All percentages relative to total starters ({f.started || 0} users)</p>
          <div className="glass rounded-2xl p-5 space-y-5">
            {[
              { label: "Basics (step 0)",    key: "step_0",    rateKey: null,           pct: f.step_0_pct    },
              { label: "Intention (step 1)", key: "step_1",    rateKey: "s0_to_s1",     pct: f.step_1_pct    },
              { label: "Values (step 2)",    key: "step_2",    rateKey: "s1_to_s2",     pct: f.step_2_pct    },
              { label: "Video (step 3)",     key: "step_3",    rateKey: "s2_to_s3",     pct: f.step_3_pct    },
              { label: "Completed",          key: "completed", rateKey: "s3_to_complete", pct: f.completed_pct },
            ].map(({ label, key, rateKey, pct: p }, i, arr) => (
              <FunnelBar
                key={key}
                label={label}
                count={f[key] || 0}
                pct={p ?? 0}
                stepRate={rateKey ? r[rateKey] : null}
                isLast={i === arr.length - 1}
              />
            ))}
            <div className="pt-2 border-t border-white/8 text-white/30 text-xs">
              {f.started || 0} total tracked entries
            </div>
          </div>
        </section>

        {/* ── Section 4: Traffic Quality ────────────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-4">Traffic Quality (last 7 days)</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-5">
              <div className="text-sm text-white/50 mb-3">All Traffic</div>
              <div className="flex gap-3">
                <TrafficBadge type="auth" count={t.auth} />
                <TrafficBadge type="anon" count={t.anon} />
                <TrafficBadge type="bot"  count={t.bot}  />
              </div>
              <div className="mt-4 space-y-2">
                {(['auth', 'anon', 'bot'] ).map((k) => {
                  const colors = { auth: "bg-emerald-400", anon: "bg-[#D4AF37]", bot: "bg-red-400" };
                  const v = t[k] || 0;
                  const p = totalTraffic > 0 ? Math.round(v / totalTraffic * 100) : 0;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-white/50 capitalize">{k}</span>
                        <span className="text-white/35">{p}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/8">
                        <div className={`h-1.5 rounded-full ${colors[k]} transition-all`} style={{ width: `${p}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass rounded-2xl p-5 space-y-5">
              <div>
                <div className="text-sm text-white/50 mb-2">Onboarding Starts by type</div>
                <div className="flex gap-3">
                  <TrafficBadge type="auth" count={startSplit.auth} />
                  <TrafficBadge type="anon" count={startSplit.anon} />
                  <TrafficBadge type="bot"  count={startSplit.bot}  />
                </div>
              </div>
              <div className="border-t border-white/8 pt-4">
                <div className="text-sm text-white/50 mb-2">Onboarding Blocks by type</div>
                <div className="flex gap-3">
                  <TrafficBadge type="auth" count={blockSplit.auth} />
                  <TrafficBadge type="anon" count={blockSplit.anon} />
                  <TrafficBadge type="bot"  count={blockSplit.bot}  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 5: Anomaly Signals ────────────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-4">
            Anomaly Signals
            {an.bot_spike && (
              <span className="ml-3 text-sm text-red-400 font-sans font-normal">⚠ Bot spike detected</span>
            )}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-2xl p-5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Bot % today</div>
              <div className={`font-display text-4xl ${an.bot_pct_24h > 40 ? "text-red-400" : an.bot_pct_24h > 20 ? "text-orange-400" : "text-white"}`}>
                {an.bot_pct_24h ?? 0}%
              </div>
              <div className="text-xs text-white/30 mt-1">prev: {an.bot_pct_prev_24h ?? 0}%</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Bot events today</div>
              <div className="font-display text-4xl">{an.bot_24h ?? 0}</div>
              <div className="text-xs text-white/30 mt-1">of {an.total_24h ?? 0} total</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">Bot % overall (7d)</div>
              <div className={`font-display text-4xl ${botPct > 40 ? "text-red-400" : botPct > 20 ? "text-orange-400" : "text-white"}`}>
                {botPct}%
              </div>
              <div className="text-xs text-white/30 mt-1">{t.bot || 0} bot events</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2">High-velocity IPs</div>
              <div className={`font-display text-4xl ${(an.high_velocity_ips || 0) > 0 ? "text-orange-400" : "text-white"}`}>
                {an.high_velocity_ips ?? 0}
              </div>
              <div className="text-xs text-white/30 mt-1">&gt;10 attempts/hr</div>
            </div>
          </div>
        </section>

        {/* ── Section 6: Blocked Reason Intelligence ────────────────────────── */}
        {data.error_breakdown?.length > 0 && (
          <section>
            <h2 className="font-display text-xl mb-4">Blocked Reason Intelligence</h2>
            <div className="glass rounded-2xl p-5">
              <div className="space-y-3">
                {data.error_breakdown.map((row) => {
                  const maxCount = data.error_breakdown[0].count;
                  const pct = maxCount > 0 ? Math.round(row.count / maxCount * 100) : 0;
                  return (
                    <div key={row.error}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-mono text-[#F2DE94] text-xs">{row.error}</span>
                        <span className="text-white/50">{row.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/8">
                        <div className="h-1.5 rounded-full bg-[#D4AF37] transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ── Section 7: Recovery Opportunity ──────────────────────────────── */}
        <section>
          <h2 className="font-display text-xl mb-4">Recovery Opportunity</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard label="Emails Captured" value={at.total}      sub="real users only" />
            <KPICard label="Unresolved"       value={at.unresolved} sub="still pending"   />
            <KPICard label="Recovered"        value={at.resolved}   sub="returned"        />
            <KPICard label="Recovery Rate"    value={`${at.recovery_rate ?? 0}%`} accent  />
          </div>
        </section>

        {/* ── Section 8: Recent Drop-off Table ─────────────────────────────── */}
        {data.recent?.length > 0 && (
          <section>
            <h2 className="font-display text-xl mb-4">Recent Drop-offs</h2>
            <div className="overflow-x-auto rounded-2xl border border-white/8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/8">
                    {["Email", "Step", "Reason", "Status", "Time"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((row, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-2.5 font-mono text-xs text-white/70">{row.email}</td>
                      <td className="px-4 py-2.5 text-white/50">{row.step}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[rgba(212,175,55,0.1)] text-[#F2DE94]">
                          {row.error}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {row.resolved
                          ? <span className="text-emerald-400 text-xs">recovered</span>
                          : <span className="text-white/35 text-xs">pending</span>}
                      </td>
                      <td className="px-4 py-2.5 text-white/35 text-xs whitespace-nowrap">
                        {(row.created_at || "").replace("T", " ").slice(0, 16)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>
    </AdminShell>
  );
}
